import { NgClass } from '@angular/common';
import { Component, computed, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { BaseComponent } from 'src/app/components/_base/base.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ChartService } from 'src/app/services/chart/chart.service';
import { LayoutService } from 'src/app/services/layout/layout.service';
import { Path, RoutePath } from 'src/app/shared/route';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { featherChevronRight } from '@ng-icons/feather-icons';
import { UploadChartImageComponent } from '../upload-chart-image/upload-chart-image.component';
import { Category } from 'src/app/types/category';
import { TranslatePipe, TranslateService, _ } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { captureException } from '@sentry/angular';
import { CtaDialogData, CtaDialogOutput } from '../cta-dialog/types';

type ProductCategory = 'clothing-apparel' | 'shoes' | 'accessories' | 'other';

const ProductCategory: Record<ProductCategory, ProductCategory> = {
  'clothing-apparel': 'clothing-apparel',
  shoes: 'shoes',
  accessories: 'accessories',
  other: 'other',
};

@Component({
  templateUrl: './dashboard.component.html',
  standalone: true,
  selector: 'app-dashboard',
  imports: [
    NgClass,
    NgIconComponent,
    UploadChartImageComponent,
    TranslatePipe,
    MatButtonToggleModule,
    RouterLink,
    MatIconModule,
  ],
  viewProviders: [provideIcons({ featherChevronRight })],
  styles: `
    .category-container {
      --mat-standard-button-toggle-text-color: rgb(var(--color-primary));

      mat-button-toggle-group {
        @apply flex-wrap gap-4 border-none;

        .mat-button-toggle {
          @apply rounded-2xl border border-border-3;
        }
      }

      mat-pseudo-checkbox {
        @apply hidden;
      }
    }
  `,
  encapsulation: ViewEncapsulation.None,
})
export class DashboardComponent extends BaseComponent implements OnInit {
  readonly layoutService = inject(LayoutService);
  readonly chartService = inject(ChartService);
  readonly router = inject(Router);
  readonly snackbar = inject(MatSnackBar);
  readonly dialog = inject(MatDialog);
  readonly translate = inject(TranslateService);

  readonly notify = signal(false);
  readonly loading = signal(false);
  readonly productCategory = signal<ProductCategory | undefined>(undefined);

  readonly ProductRoute = RoutePath.getPath(Path['products']).fullPath;
  readonly productCategories: {
    label: string;
    value: ProductCategory;
    highlights: number[];
  }[] = [
    {
      label: 'page.dashboard.category-clothing-apparel',
      value: ProductCategory['clothing-apparel'],
      highlights: [0, 1],
    },
    {
      label: 'page.dashboard.category-shoes',
      value: ProductCategory['shoes'],
      highlights: [1],
    },
    {
      label: 'page.dashboard.category-accessories',
      value: ProductCategory['accessories'],
      highlights: [1],
    },
    {
      label: 'page.dashboard.category-other',
      value: ProductCategory['other'],
      highlights: [0, 1],
    },
  ] as const;

  readonly hightlighting = computed(() => {
    return this.productCategories.find((category) => category.value === this.productCategory())?.highlights;
  });

  ngOnInit() {
    this.sub$ = this.layoutService.notifyDashboard$.subscribe(() => {
      this.notify.set(true);

      setTimeout(() => {
        this.notify.set(false);
      }, 300);
    });
  }

  onCategoryCreated(category: Category) {
    this.chartService
      .createChart({
        category: category._id,
        measurementVisibility: category.dimensions.reduce<Record<string, Record<string, boolean>>>((acc, dimension) => {
          if (!acc[category._id]) acc[category._id] = {};

          acc[category._id][dimension._id] = true;

          return acc;
        }, {}),
      })
      .subscribe({
        next: (chart) => {
          this.loading.set(false);
          this.router.navigate(
            RoutePath.getPath(Path['charts/:id']).generate({
              id: chart._id,
            }),
            {
              queryParams: {
                customize: 'true',
              },
            },
          );
        },
        error: (error) => {
          captureException(error);
          this.loading.set(false);
          this.snackbar.open(
            this.translate.instant(_('error.error-occured')),
            this.translate.instant(_('common.close')),
            {
              panelClass: 'snackbar-error',
            },
          );
        },
      });
  }

  onCreateTableChart() {
    if (this.loading()) return;

    this.loading.set(true);

    this.sub$ = this.chartService.createTableChart({}).subscribe({
      next: ({ chart }) => {
        this.loading.set(false);
        this.router.navigate(
          RoutePath.getPath(Path['table-chart/:id']).generate({
            id: chart._id,
          }),
        );
      },
      error: (error) => {
        captureException(error);
        this.loading.set(false);
        this.snackbar.open(
          this.translate.instant(_('error.error-occured')),
          this.translate.instant(_('common.close')),
          {
            panelClass: 'snackbar-error',
          },
        );
      },
    });
  }

  async openCtaDialog() {
    const Component = await import('src/app/components/cta-dialog/cta-dialog.component').then(
      (c) => c.CtaDialogComponent,
    );

    this.sub$ = this.dialog
      .open<unknown, CtaDialogData, CtaDialogOutput>(Component, {
        data: {
          allowUpload: true,
          // allowSync: true,
        },
      })
      .afterClosed()
      .subscribe((output) => {
        if (output && output.chart)
          this.router.navigate(
            RoutePath.getPath(Path['charts/:id']).generate({
              id: output.chart._id,
            }),
          );
      });
  }

  onSelectProductCategory(category: ProductCategory | undefined) {
    if (!category) return;

    if (this.productCategory() === category) this.productCategory.set(undefined);
    else this.productCategory.set(category);
  }
}
